/**
 * Teen Zone Landing Page
 * Vibrant, empowering entry point for the teen section with feature navigation.
 * Uses TeenThemeContext for customizable look and feel based on age group.
 */

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  ArrowLeft, 
  Rocket, 
  Star, 
  Headphones, 
  RefreshCw, 
  Share2,
  Zap,
  Lock,
  Shield,
  Palette
} from "lucide-react";
import axios from "axios";
import ParentEmailModal from "../../components/ParentEmailModal";
import { TeenThemeProvider, useTeenTheme } from "../../contexts/TeenThemeContext";
import TeenThemeSelector from "../../components/TeenThemeSelector";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Inner component that uses theme context
const TeenLandingContent = () => {
  const navigate = useNavigate();
  const { 
    theme, 
    currentPalette, 
    isOnboarded,
    showThemeSelector,
    setShowThemeSelector 
  } = useTeenTheme();
  
  const [isVerified, setIsVerified] = useState(null);
  const [showAgeVerify, setShowAgeVerify] = useState(false);
  const [showParentEmail, setShowParentEmail] = useState(false);
  const [parentVerified, setParentVerified] = useState(false);
  const [birthYear, setBirthYear] = useState("");
  const [birthMonth, setBirthMonth] = useState("");
  const [verifyError, setVerifyError] = useState("");
  const [stats, setStats] = useState(null);
  
  const getToken = () => localStorage.getItem("reclaim_token");

  // Get theme colors with fallbacks
  const colors = {
    base: theme?.colors?.base || '#0f0f23',
    surface: theme?.colors?.surface || 'rgba(255,255,255,0.05)',
    primary: currentPalette?.primary || theme?.colors?.primary || '#818CF8',
    accent: currentPalette?.accent || theme?.colors?.accent || '#F472B6',
    textPrimary: theme?.colors?.textPrimary || '#ffffff',
    textSecondary: theme?.colors?.textSecondary || 'rgba(255,255,255,0.6)',
    textMuted: theme?.colors?.textMuted || 'rgba(255,255,255,0.4)',
    border: theme?.colors?.border || 'rgba(255,255,255,0.1)',
  };

  useEffect(() => {
    checkAccess();
  }, []);

  const checkAccess = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/teen/access-status`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setIsVerified(res.data.verified);
      
      if (res.data.verified) {
        loadStats();
        checkParentStatus();
      }
    } catch (err) {
      console.error("Error checking access:", err);
      setIsVerified(false);
    }
  };

  const checkParentStatus = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/parent/teen-status`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setParentVerified(res.data.parent_verified);
      
      if (!res.data.parent_verified && !res.data.parent_email) {
        setTimeout(() => setShowParentEmail(true), 1500);
      }
    } catch (err) {
      console.error("Error checking parent status:", err);
    }
  };

  const loadStats = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/teen/stats`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setStats(res.data);
    } catch (err) {
      console.error("Error loading stats:", err);
    }
  };

  const handleVerifyAge = async () => {
    setVerifyError("");
    
    if (!birthYear || !birthMonth) {
      setVerifyError("Please enter your birth month and year");
      return;
    }
    
    const token = getToken();
    if (!token) {
      setVerifyError("Please log in again to continue");
      return;
    }
    
    try {
      await axios.post(
        `${API_URL}/api/teen/verify-age`,
        { birth_year: parseInt(birthYear), birth_month: parseInt(birthMonth) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setIsVerified(true);
      setShowAgeVerify(false);
      loadStats();
      setTimeout(() => setShowParentEmail(true), 1000);
    } catch (err) {
      console.error("Verify age error:", err);
      if (err.response?.status === 401) {
        setVerifyError("Session expired. Please log in again.");
        localStorage.removeItem("reclaim_token");
        setTimeout(() => navigate("/login"), 2000);
      } else if (err.response?.status === 403) {
        setVerifyError("You must be 13 or older to access the Teen Zone");
      } else {
        setVerifyError(err.response?.data?.detail || "Verification failed. Please try again.");
      }
    }
  };

  // Feature cards - colors adapt to theme
  const features = [
    {
      id: "challenge",
      title: "21-Day Journey",
      description: "Transform your mindset with daily challenges",
      icon: Rocket,
      gradient: `linear-gradient(135deg, ${colors.primary}, ${colors.accent})`,
      path: "/teen/challenge",
      badge: stats?.days_completed ? `Day ${stats.days_completed + 1}` : "Start Now"
    },
    {
      id: "affirmations",
      title: "Power Affirmations",
      description: "Boost confidence and focus",
      icon: Star,
      gradient: `linear-gradient(135deg, #F59E0B, #F97316)`,
      path: "/teen/affirmations"
    },
    {
      id: "subliminals",
      title: "Study & Focus",
      description: "Background audio for concentration",
      icon: Headphones,
      gradient: `linear-gradient(135deg, #3B82F6, #06B6D4)`,
      path: "/teen/subliminals"
    },
    {
      id: "reset",
      title: "Quick Reset",
      description: "Calm your mind in seconds",
      icon: RefreshCw,
      gradient: `linear-gradient(135deg, #10B981, #14B8A6)`,
      path: "/teen/reset"
    }
  ];

  // Loading state
  if (isVerified === null) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center"
        style={{ background: colors.base }}
      >
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
        >
          <Zap size={40} style={{ color: colors.primary }} />
        </motion.div>
      </div>
    );
  }

  // Age verification - not yet verified, show entry screen
  if (!isVerified && !showAgeVerify) {
    return (
      <div 
        className="min-h-screen flex flex-col"
        style={{ background: colors.base }}
      >
        <div className="p-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 transition-colors"
            style={{ color: colors.textSecondary }}
          >
            <ArrowLeft size={20} />
            <span>Back</span>
          </button>
        </div>

        <div className="flex-1 flex flex-col items-center justify-center px-6 text-center">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", stiffness: 200 }}
            className="relative mb-8"
          >
            <div 
              className="w-28 h-28 rounded-3xl flex items-center justify-center shadow-2xl"
              style={{ 
                background: `linear-gradient(135deg, ${colors.primary}, ${colors.accent})`,
                boxShadow: `0 25px 50px -12px ${colors.primary}40`
              }}
            >
              <Rocket size={48} className="text-white" />
            </div>
            <motion.div
              animate={{ scale: [1, 1.2, 1], opacity: [0.5, 0.8, 0.5] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="absolute inset-0 w-28 h-28 rounded-3xl blur-xl -z-10"
              style={{ background: `${colors.primary}30` }}
            />
          </motion.div>

          <h1 
            className="text-3xl font-bold mb-3"
            style={{ color: colors.textPrimary }}
          >
            Teen Zone
          </h1>
          <p 
            className="mb-8 max-w-sm"
            style={{ color: colors.textSecondary }}
          >
            A space built just for you. Build confidence, crush exams, and become unstoppable.
          </p>

          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setShowAgeVerify(true)}
            className="px-8 py-4 rounded-2xl text-white font-semibold shadow-lg flex items-center gap-3"
            style={{ 
              background: `linear-gradient(135deg, ${colors.primary}, ${colors.accent})`,
              boxShadow: `0 10px 40px -10px ${colors.primary}50`
            }}
            data-testid="teen-enter-btn"
          >
            <Lock size={20} />
            <span>Enter Teen Zone</span>
          </motion.button>

          <p 
            className="text-sm mt-6"
            style={{ color: colors.textMuted }}
          >
            For ages 13 and up
          </p>
        </div>
      </div>
    );
  }

  // Age verification form
  if (showAgeVerify && !isVerified) {
    return (
      <div 
        className="min-h-screen flex flex-col"
        style={{ background: colors.base }}
      >
        <div className="p-4">
          <button
            onClick={() => setShowAgeVerify(false)}
            className="flex items-center gap-2"
            style={{ color: colors.textSecondary }}
          >
            <ArrowLeft size={20} />
            <span>Back</span>
          </button>
        </div>

        <div className="flex-1 flex flex-col items-center justify-center px-6">
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="w-full max-w-sm"
          >
            <h2 
              className="text-2xl font-bold text-center mb-2"
              style={{ color: colors.textPrimary }}
            >
              Quick Check
            </h2>
            <p 
              className="text-center mb-8"
              style={{ color: colors.textSecondary }}
            >
              When were you born?
            </p>

            <div className="space-y-4 mb-6">
              <div>
                <label 
                  className="text-sm mb-2 block"
                  style={{ color: colors.textSecondary }}
                >
                  Month
                </label>
                <select
                  value={birthMonth}
                  onChange={(e) => setBirthMonth(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border focus:outline-none transition-colors"
                  style={{ 
                    background: colors.surface,
                    borderColor: colors.border,
                    color: colors.textPrimary
                  }}
                  data-testid="birth-month-select"
                >
                  <option value="">Select month</option>
                  {Array.from({ length: 12 }, (_, i) => (
                    <option key={i + 1} value={i + 1}>
                      {new Date(2000, i).toLocaleString('default', { month: 'long' })}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label 
                  className="text-sm mb-2 block"
                  style={{ color: colors.textSecondary }}
                >
                  Year
                </label>
                <select
                  value={birthYear}
                  onChange={(e) => setBirthYear(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border focus:outline-none"
                  style={{ 
                    background: colors.surface,
                    borderColor: colors.border,
                    color: colors.textPrimary
                  }}
                  data-testid="birth-year-select"
                >
                  <option value="">Select year</option>
                  {Array.from({ length: 20 }, (_, i) => {
                    const year = new Date().getFullYear() - 10 - i;
                    return (
                      <option key={year} value={year}>{year}</option>
                    );
                  })}
                </select>
              </div>
            </div>

            {verifyError && (
              <p className="text-red-400 text-sm text-center mb-4">{verifyError}</p>
            )}

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleVerifyAge}
              className="w-full py-4 rounded-xl text-white font-semibold shadow-lg"
              style={{ 
                background: `linear-gradient(135deg, ${colors.primary}, ${colors.accent})`,
                boxShadow: `0 10px 40px -10px ${colors.primary}50`
              }}
              data-testid="verify-age-btn"
            >
              Continue
            </motion.button>
          </motion.div>
        </div>
      </div>
    );
  }

  // Main Teen Zone content
  return (
    <div 
      className="min-h-screen"
      style={{ background: colors.base }}
    >
      {/* Header */}
      <div className="p-4 flex items-center justify-between">
        <button
          onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/"))}
          data-testid="teen-back-btn"
          style={{ color: colors.textSecondary }}
        >
          <ArrowLeft size={20} />
        </button>
        
        <div className="flex items-center gap-2">
          {/* Theme selector button */}
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setShowThemeSelector(true)}
            className="p-2 rounded-lg transition-colors"
            style={{ 
              background: colors.surface,
              border: `1px solid ${colors.border}`
            }}
            data-testid="open-theme-selector"
          >
            <Palette size={18} style={{ color: colors.textSecondary }} />
          </motion.button>
          
          {/* XP Badge */}
          {stats?.total_xp > 0 && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="flex items-center gap-2 px-3 py-1.5 rounded-full border"
              style={{ 
                background: `${colors.primary}15`,
                borderColor: `${colors.primary}30`
              }}
            >
              <Zap size={14} style={{ color: colors.primary }} />
              <span 
                className="font-semibold text-sm"
                style={{ color: colors.primary }}
              >
                {stats.total_xp} XP
              </span>
            </motion.div>
          )}
        </div>
      </div>

      {/* Hero */}
      <div className="px-6 pt-4 pb-8 text-center">
        <motion.div
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="relative inline-block mb-4"
        >
          <div 
            className="w-20 h-20 rounded-2xl flex items-center justify-center shadow-xl"
            style={{ 
              background: `linear-gradient(135deg, ${colors.primary}, ${colors.accent})`,
              boxShadow: `0 20px 40px -10px ${colors.primary}40`
            }}
          >
            <Rocket size={36} className="text-white" />
          </div>
        </motion.div>

        <motion.h1
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="text-3xl font-bold mb-2"
          style={{ color: colors.textPrimary }}
        >
          Teen Zone
        </motion.h1>
        
        <motion.p
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
          style={{ color: colors.textSecondary }}
        >
          Your space. Your growth. Your power.
        </motion.p>
      </div>

      {/* Stats Row */}
      {stats && (stats.days_completed > 0 || stats.current_streak > 0) && (
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="px-6 mb-6"
        >
          <div className="flex justify-center gap-6">
            {stats.days_completed > 0 && (
              <div className="text-center">
                <div 
                  className="text-2xl font-bold"
                  style={{ color: colors.primary }}
                >
                  {stats.days_completed}
                </div>
                <div 
                  className="text-xs"
                  style={{ color: colors.textMuted }}
                >
                  Days Done
                </div>
              </div>
            )}
            {stats.current_streak > 0 && (
              <div className="text-center">
                <div 
                  className="text-2xl font-bold"
                  style={{ color: colors.accent }}
                >
                  {stats.current_streak}
                </div>
                <div 
                  className="text-xs"
                  style={{ color: colors.textMuted }}
                >
                  Day Streak
                </div>
              </div>
            )}
            {stats.total_resets > 0 && (
              <div className="text-center">
                <div className="text-2xl font-bold text-emerald-400">{stats.total_resets}</div>
                <div 
                  className="text-xs"
                  style={{ color: colors.textMuted }}
                >
                  Resets
                </div>
              </div>
            )}
          </div>
        </motion.div>
      )}

      {/* Feature Cards */}
      <div className="px-6 pb-8 space-y-4">
        {features.map((feature, index) => {
          const Icon = feature.icon;
          return (
            <motion.button
              key={feature.id}
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.2 + index * 0.1 }}
              whileHover={{ scale: 1.02, x: 4 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate(feature.path)}
              className="w-full p-5 rounded-2xl shadow-xl flex items-center gap-4 text-left"
              style={{ 
                background: feature.gradient,
                boxShadow: `0 10px 40px -10px ${colors.primary}30`
              }}
              data-testid={`teen-feature-${feature.id}`}
            >
              <div className="w-14 h-14 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                <Icon size={28} className="text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-bold text-white mb-0.5">{feature.title}</h3>
                <p className="text-white/80 text-sm">{feature.description}</p>
              </div>
              {feature.badge && (
                <span className="px-3 py-1 rounded-full bg-white/20 text-white text-xs font-semibold">
                  {feature.badge}
                </span>
              )}
            </motion.button>
          );
        })}

        {/* Share the Moment */}
        <motion.button
          initial={{ x: -20, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: 0.6 }}
          whileHover={{ scale: 1.02, x: 4 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => navigate("/teen/share")}
          className="w-full p-4 rounded-2xl border flex items-center gap-4"
          style={{ 
            background: colors.surface,
            borderColor: colors.border
          }}
          data-testid="teen-feature-share"
        >
          <div 
            className="w-12 h-12 rounded-xl flex items-center justify-center"
            style={{ background: `${colors.primary}20` }}
          >
            <Share2 size={22} style={{ color: colors.primary }} />
          </div>
          <div className="flex-1 text-left">
            <h3 
              className="font-semibold"
              style={{ color: colors.textPrimary }}
            >
              Share the Moment
            </h3>
            <p 
              className="text-sm"
              style={{ color: colors.textMuted }}
            >
              Celebrate your wins
            </p>
          </div>
        </motion.button>
      </div>

      {/* Bottom Encouragement */}
      <div className="px-6 pb-8 text-center">
        <p 
          className="text-sm italic"
          style={{ color: colors.textMuted }}
        >
          "You're capable of more than you know."
        </p>
      </div>
      
      {/* Parent Verification Status Banner */}
      {isVerified && !parentVerified && (
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="fixed bottom-4 left-4 right-4 mx-auto max-w-md"
        >
          <button
            onClick={() => setShowParentEmail(true)}
            className="w-full p-4 rounded-xl border flex items-center gap-3"
            style={{ 
              background: `linear-gradient(135deg, ${colors.primary}15, ${colors.accent}15)`,
              borderColor: `${colors.primary}30`
            }}
            data-testid="parent-verify-banner"
          >
            <div 
              className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ background: `${colors.primary}30` }}
            >
              <Shield size={18} style={{ color: colors.primary }} />
            </div>
            <div className="flex-1 text-left">
              <p 
                className="text-sm font-medium"
                style={{ color: colors.textPrimary }}
              >
                Parent Verification Pending
              </p>
              <p 
                className="text-xs"
                style={{ color: colors.textSecondary }}
              >
                Tap to send verification email
              </p>
            </div>
          </button>
        </motion.div>
      )}
      
      {/* Parent Email Modal */}
      <ParentEmailModal 
        isOpen={showParentEmail}
        onClose={() => setShowParentEmail(false)}
        onSuccess={() => {
          setShowParentEmail(false);
          checkParentStatus();
        }}
      />
      
      {/* Theme Selector Modal */}
      <TeenThemeSelector 
        isOpen={showThemeSelector}
        onClose={() => setShowThemeSelector(false)}
      />
    </div>
  );
};

// Main component wraps content with theme provider
const TeenLandingPage = () => {
  return (
    <TeenThemeProvider>
      <TeenLandingContent />
    </TeenThemeProvider>
  );
};

export default TeenLandingPage;
